import { LinkOutlined } from '@ant-design/icons';
import type { Settings as LayoutSettings } from '@ant-design/pro-components';
import { SettingDrawer } from '@ant-design/pro-components';
import type { RequestConfig, RunTimeLayoutConfig } from '@umijs/max';
import { history, Link } from '@umijs/max';
import React from 'react';
import {
  AvatarDropdown,
  AvatarName,
  Footer,
  Question,
  SelectLang,
} from '@/components';
import defaultSettings from '../config/defaultSettings';
import { errorConfig } from './requestErrorConfig';
import '@ant-design/v5-patch-for-react-19';
import { getAdminToken } from '@/utils/auth';
import {
  isLoginPath,
  isUnauthorizedError,
  isUnauthorizedResponseData,
  redirectToLogin,
} from '@/utils/authRedirect';
import { getCurrentAdminUserInfo } from '@/services/smart-tower/admin';

declare const __API_BASE_URL__: string;

const isDev = process.env.NODE_ENV === 'development' || process.env.CI;
const loginPath = '/user/login';
const isLoginDisabled = (globalThis as any).__LOGIN_DISABLED__ === true;
const API_BASE_URL = __API_BASE_URL__ || '';

function buildMockAdminUser(): API.CurrentUser {
  return {
    name: '管理员',
    userid: 'admin',
    email: 'admin@zhinengta.com',
    access: 'admin',
    avatar:
      'https://gw.alipayobjects.com/zos/antfincdn/XAosXuNZyF/BiazfanxmamNRoxxVxka.png',
  } as API.CurrentUser;
}

/**
 * @see https://umijs.org/docs/api/runtime-config#getinitialstate
 * */
export async function getInitialState(): Promise<{
  settings?: Partial<LayoutSettings>;
  currentUser?: API.CurrentUser;
  loading?: boolean;
  fetchUserInfo?: () => Promise<API.CurrentUser | undefined>;
}> {
  const fetchUserInfo = async () => {
    const token = getAdminToken();
    if (!token) return undefined;
    try {
      const res = await getCurrentAdminUserInfo({ skipErrorHandler: true });
      const u = res.data;
      return {
        name: u?.nickname || '管理员',
        userid: String(u?.id ?? ''),
        email: '',
        access: u?.role || 'admin',
        avatar: u?.avatar || undefined,
        phone: u?.phone,
      } as API.CurrentUser;
    } catch (_e) {
      redirectToLogin({ reason: '登录状态已失效，请重新登录' });
      return undefined;
    }
  };

  // 线上免登录：直接注入一个管理员用户（不请求后端 /api/currentUser）
  if (isLoginDisabled) {
    const currentUser = buildMockAdminUser();
    return {
      fetchUserInfo: async () => currentUser,
      currentUser,
      settings: defaultSettings as Partial<LayoutSettings>,
    };
  }

  // 已登录：存在 token，则注入一个管理员用户（后续可替换为真实 currentUser 接口）
  const token = getAdminToken();
  if (token) {
    const currentUser = buildMockAdminUser();
    return {
      fetchUserInfo,
      currentUser,
      settings: defaultSettings as Partial<LayoutSettings>,
    };
  }

  // 如果不是登录页面，执行
  const { location } = history;
  if (
    ![loginPath, '/user/register', '/user/register-result'].includes(
      location.pathname,
    )
  ) {
    const currentUser = await fetchUserInfo();
    return {
      fetchUserInfo,
      currentUser,
      settings: defaultSettings as Partial<LayoutSettings>,
    };
  }
  return {
    fetchUserInfo,
    settings: defaultSettings as Partial<LayoutSettings>,
  };
}

// ProLayout 支持的api https://procomponents.ant.design/components/layout
export const layout: RunTimeLayoutConfig = ({
  initialState,
  setInitialState,
}) => {
  return {
    actionsRender: () => [
      <Question key="doc" />,
      <SelectLang key="SelectLang" />,
    ],
    avatarProps: {
      src: initialState?.currentUser?.avatar,
      title: <AvatarName />,
      render: (_, avatarChildren) => {
        return <AvatarDropdown>{avatarChildren}</AvatarDropdown>;
      },
    },
    waterMarkProps: {
      content: initialState?.currentUser?.name,
    },
    footerRender: () => <Footer />,
    onPageChange: () => {
      const { location } = history;
      // token 为空时必须回登录页（不能依赖 currentUser：过期清 token 后 mock 用户仍在 initialState 会导致无法跳转）
      if (isLoginDisabled) return;
      const token = getAdminToken();
      if (!token && !isLoginPath(location.pathname)) {
        setInitialState((s) => ({
          ...s,
          currentUser: undefined,
        }));
        history.push(loginPath);
      }
    },
    bgLayoutImgList: [
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/D2LWSqNny4sAAAAAAAAAAAAAFl94AQBr',
        left: 85,
        bottom: 100,
        height: '303px',
      },
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/C2TWRpJpiC0AAAAAAAAAAAAAFl94AQBr',
        bottom: -68,
        right: -45,
        height: '303px',
      },
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/F6vSTbj8KpYAAAAAAAAAAAAAFl94AQBr',
        bottom: 0,
        left: 0,
        width: '331px',
      },
    ],
    links: isDev
      ? [
          <Link key="openapi" to="/umi/plugin/openapi" target="_blank">
            <LinkOutlined />
            <span>OpenAPI 文档</span>
          </Link>,
        ]
      : [],
    menuHeaderRender: undefined,
    // 自定义 403 页面
    // unAccessible: <div>unAccessible</div>,
    // 增加一个 loading 的状态
    childrenRender: (children) => {
      // if (initialState?.loading) return <PageLoading />;
      return (
        <>
          {children}
          {isDev && (
            <SettingDrawer
              disableUrlParams
              enableDarkTheme
              settings={initialState?.settings}
              onSettingChange={(settings) => {
                setInitialState((preInitialState) => ({
                  ...preInitialState,
                  settings,
                }));
              }}
            />
          )}
        </>
      );
    },
    ...initialState?.settings,
  };
};

/**
 * @name request 配置，可以配置错误处理
 * 它基于 axios 和 ahooks 的 useRequest 提供了一套统一的网络请求和错误处理方案。
 * @doc https://umijs.org/docs/max/request#配置
 */
export const request: RequestConfig = {
  ...errorConfig,
  errorConfig: {
    errorThrower: errorConfig.errorConfig?.errorThrower,
    errorHandler: (error: any, opts: any) => {
      // 必须在 skipErrorHandler 之前处理：smart-tower 等接口统一 skipErrorHandler，
      // 否则会直接 throw，HTTP 401 时永远不会跳转登录。
      if (isUnauthorizedError(error)) {
        redirectToLogin({
          reason:
            error?.response?.data?.msg ||
            error?.data?.msg ||
            error?.message ||
            '登录已失效，请重新登录',
        });
        return;
      }
      if (opts?.skipErrorHandler) throw error;
      return errorConfig.errorConfig?.errorHandler?.(error, opts);
    },
  },
  ...(API_BASE_URL
    ? {
        baseURL: API_BASE_URL.replace(/\/$/, ''),
      }
    : {}),
  requestInterceptors: [
    (url, options) => {
      const token = getAdminToken();
      if (!token) return { url, options };
      const headers = {
        ...(options.headers || {}),
        Authorization: `Bearer ${token}`,
      };
      return { url, options: { ...options, headers } };
    },
  ],
  responseInterceptors: [
    ...(errorConfig.responseInterceptors || []),
    (response) => {
      const r = response as any;
      if (r?.status === 401) {
        redirectToLogin({
          reason: r?.data?.msg || '登录已失效，请重新登录',
        });
        return Promise.reject(Object.assign(new Error('Unauthorized'), { code: 401 }));
      }
      if (isUnauthorizedResponseData(r?.data)) {
        redirectToLogin({
          reason: r?.data?.msg || '登录已失效，请重新登录',
        });
        return Promise.reject(Object.assign(new Error('Unauthorized'), { code: 401 }));
      }
      return response;
    },
  ],
};
